// Phone Input initialization using intl-tel-input
// This script auto-initializes all phone input fields on the page

import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";

// Store instances and their cleanup functions for proper memory management
const phoneInputInstances = new Map();

function detectCountryFromBrowser() {
  const lang = navigator.language || navigator.userLanguage || "";
  const parts = lang.split("-");

  // Try to get country from language code (e.g., "en-US" -> "us")
  if (parts.length >= 2) {
    return parts[1].toLowerCase();
  }

  // Map language codes to likely countries
  const langToCountry = {
    nb: "no",
    nn: "no",
    no: "no",
    sv: "se",
    da: "dk",
    fi: "fi",
    de: "de",
    nl: "nl",
    pl: "pl",
    uk: "ua",
    en: "gb",
    es: "es",
    fr: "fr",
    it: "it",
    pt: "pt",
  };

  const langCode = parts[0] ? parts[0].toLowerCase() : null;
  return langToCountry[langCode] || "no";
}

export function initPhoneInput(container) {
  const input = container.querySelector("input[type='tel']");
  const hiddenInput = container.querySelector(
    "input[type='hidden'][data-phone-hidden]"
  );

  if (!input || phoneInputInstances.has(input)) return;

  const dataCountry = container.dataset.defaultCountry;
  const defaultCountry =
    dataCountry && dataCountry !== "auto"
      ? dataCountry
      : detectCountryFromBrowser();

  // Fallback preferred countries - should match PhoneInputHelper::PRIORITY_COUNTRIES in Ruby
  const preferredCountries = (
    container.dataset.preferredCountries || "no,se,dk,fi,gb,us,de,nl,pl"
  ).split(",");

  const iti = intlTelInput(input, {
    initialCountry: defaultCountry,
    preferredCountries: preferredCountries,
    separateDialCode: true,
    nationalMode: false,
    formatOnDisplay: true,
    autoPlaceholder: "aggressive",
    // Load utils from CDN - version matches package.json (25.14.0)
    // Security consideration: For improved security, consider:
    // 1. Self-hosting the utils script in public/assets
    // 2. Adding SRI hash verification
    // 3. Using dynamic import if supported in future intl-tel-input versions
    utilsScript:
      "https://cdn.jsdelivr.net/npm/intl-tel-input@25.14.0/build/js/utils.js",
  });

  // Update hidden input with full international number
  const updateHiddenInput = () => {
    if (!hiddenInput) return;

    // Get the full international number
    const number = iti.getNumber();
    if (number) {
      hiddenInput.value = number;
    } else if (input.value) {
      // Fallback: manually construct the number if utils haven't loaded
      const countryData = iti.getSelectedCountryData();
      const dialCode = countryData.dialCode || "";
      const nationalNumber = input.value.replace(/^0+/, "").replace(/\D/g, "");
      hiddenInput.value = nationalNumber ? `+${dialCode}${nationalNumber}` : "";
    } else {
      hiddenInput.value = "";
    }
  };

  // Store references to event handlers for cleanup
  const handlers = {
    change: updateHiddenInput,
    blur: updateHiddenInput,
    countrychange: updateHiddenInput,
    input: updateHiddenInput,
    keyup: updateHiddenInput,
  };

  // Add event listeners
  Object.entries(handlers).forEach(([event, handler]) => {
    input.addEventListener(event, handler);
  });

  // Track form for submit handler
  const form = input.closest("form");
  if (form) {
    form.addEventListener("submit", updateHiddenInput);
  }

  // Store instance and cleanup function
  phoneInputInstances.set(input, {
    iti,
    cleanup: () => {
      // Remove all event listeners
      Object.entries(handlers).forEach(([event, handler]) => {
        input.removeEventListener(event, handler);
      });
      if (form) {
        form.removeEventListener("submit", updateHiddenInput);
      }
      iti.destroy();
    },
  });

  // Initialize if there's already a value
  if (input.value) {
    iti.setNumber(input.value);
    updateHiddenInput();
  }

  return iti;
}

export function destroyPhoneInput(container) {
  const input = container.querySelector("input[type='tel']");
  if (!input) return;

  const instance = phoneInputInstances.get(input);
  if (instance) {
    instance.cleanup();
    phoneInputInstances.delete(input);
  }
}

export function initPhoneInputs() {
  document.querySelectorAll("[data-phone-input]").forEach(initPhoneInput);
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", initPhoneInputs);

// Also listen for Turbo/Turbolinks navigation
document.addEventListener("turbo:load", initPhoneInputs);
document.addEventListener("turbolinks:load", initPhoneInputs);

// Cleanup on Turbo navigation (before cache)
document.addEventListener("turbo:before-cache", () => {
  phoneInputInstances.forEach((instance) => instance.cleanup());
  phoneInputInstances.clear();
});

// Export for manual initialization and cleanup
window.initPhoneInput = initPhoneInput;
window.initPhoneInputs = initPhoneInputs;
window.destroyPhoneInput = destroyPhoneInput;
